import { api } from '../../../lib/api';
import type {
  BudgetPlanDepartmentRow,
  PpaDetail,
  PpaExpenditureLine,
  PpaLineItemsCreateRequest,
  PpaLineItemUpdateRequest,
  PpaListItem,
  PpaRequest,
} from './ppaTypes';

/**
 * A department's PPAs under one budget plan.
 *
 * **No `list(query)` and no `toQueryString`**, unlike every other module here: this one has
 * no index page. Both reads are scoped to a plan and arrive WHOLE, which is the 201 file's
 * own exemption — a section that arrives entire lets the panel's filter see everything there
 * is. What would end it is paging either of them server-side.
 */
export const ppasApi = {
  /**
   * The plan's DEPARTMENT ROSTER — one row per enrolled department, every one of them.
   *
   * Gated on `budget-plans.read` server-side, not on `ppas.read`: the roster IS the plan's
   * PPA tab, and a reader of plans must not need a second grant to see who is expected to
   * propose. Opening what a department actually filed is the narrower question.
   */
  roster: (budgetPlanId: string, signal?: AbortSignal) =>
    api<BudgetPlanDepartmentRow[]>(`/api/ppas/roster/${budgetPlanId}`, { signal }),

  forPlan: (budgetPlanId: string, includeDeleted = false, signal?: AbortSignal) =>
    api<PpaListItem[]>(
      `/api/ppas/plan/${budgetPlanId}${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  /**
   * Every ACTIVE budgeted line under one plan — the Expenditures roll-up.
   *
   * `ppas.read`, unlike the roster above it: the roster states how far each department has
   * got, which is part of reading a plan, where this states what every office is asking for
   * line by line, which is the PPA module's own content.
   */
  expenditures: (budgetPlanId: string, signal?: AbortSignal) =>
    api<PpaExpenditureLine[]>(`/api/ppas/expenditures/${budgetPlanId}`, { signal }),

  /**
   * One PPA and its budgeted lines.
   *
   * `includeDeleted` lists the lines somebody deleted beside the live ones, and it is what
   * makes the RESTORE reachable — without it a deleted line is invisible to every read, so
   * nothing can name one to put back.
   */
  get: (id: string, includeDeleted = false, signal?: AbortSignal) =>
    api<PpaDetail>(`/api/ppas/${id}${includeDeleted ? '?includeDeleted=true' : ''}`, { signal }),

  create: (request: PpaRequest) => api<PpaDetail>('/api/ppas', { method: 'POST', body: request }),

  /** Replaces the pairs and the lines WHOLESALE — a PPA and its lines are one transaction. */
  update: (id: string, request: PpaRequest) =>
    api<PpaDetail>(`/api/ppas/${id}`, { method: 'PUT', body: request }),

  /*
    The budgeted lines are a SUB-RESOURCE, not a field of the PPA.

    A PPA is created empty and filled a class at a time, then adjusted by a reviewer who
    never opened the department's form — so each line is addressed by its own id. Sending
    them with the PPA meant every correction to a name flagged away the generation a
    reviewer's figure was sitting on.
  */
  addLineItems: (ppaId: string, request: PpaLineItemsCreateRequest) =>
    api<PpaDetail>(`/api/ppas/${ppaId}/line-items`, { method: 'POST', body: request }),

  updateLineItem: (ppaId: string, lineItemId: string, request: PpaLineItemUpdateRequest) =>
    api<PpaDetail>(`/api/ppas/${ppaId}/line-items/${lineItemId}`, { method: 'PUT', body: request }),

  deleteLineItem: (ppaId: string, lineItemId: string) =>
    api<void>(`/api/ppas/${ppaId}/line-items/${lineItemId}`, { method: 'DELETE' }),

  restoreLineItem: (ppaId: string, lineItemId: string) =>
    api<void>(`/api/ppas/${ppaId}/line-items/${lineItemId}/restore`, { method: 'POST' }),

  /** Soft delete. Its lines are left alone, so a restore is faithful. */
  deactivate: (id: string) => api<void>(`/api/ppas/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/ppas/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/ppas/clear', { method: 'POST' }),
};
